import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { QueryClientProvider, useQuery } from '@tanstack/react-query';
import { Menu } from 'lucide-react';

import {
  almacen,
  canjearCodigo,
  cerrarSesion,
  hasSession,
  onSesionPerdida,
  recuperarSesion,
  setAccessToken,
  type Session,
} from './api';
import { iniciales } from './formato';
import { Entrada } from './pantallas/Entrada';
import { Resumen } from './pantallas/Resumen';
import { SesionActual } from './pantallas/Bienvenida';
import { Insumos } from './pantallas/Insumos';
import { Lotes } from './pantallas/Lotes';
import { Mermas } from './pantallas/Mermas';
import { CierreDeNoche } from './pantallas/CierreDeNoche';
import { Conteo } from './pantallas/Conteo';
import { Kardex } from './pantallas/Kardex';
import { Valorizacion } from './pantallas/Valorizacion';
import { Fuga } from './pantallas/Fuga';
import { Cadena } from './pantallas/Cadena';
import { Recepcion } from './pantallas/Recepcion';
import { Reposicion } from './pantallas/Reposicion';
import { Recetas } from './pantallas/Recetas';
import { CostoReal } from './pantallas/CostoReal';
import { Proveedores } from './pantallas/Proveedores';
import { Produccion } from './pantallas/Produccion';
import { Compras } from './pantallas/Compras';
import { CargaInicial } from './pantallas/CargaInicial';
import { Cuarentena } from './pantallas/Cuarentena';
import { Series } from './pantallas/Series';
import { LibroDeRecetas } from './pantallas/LibroDeRecetas';
import { LibroDeControlados } from './pantallas/LibroDeControlados';
import { CadenaDeFrio } from './pantallas/CadenaDeFrio';
import { Precios } from './pantallas/Precios';
import { SECCIONES, menuDe, seccionPermitida, type Seccion } from './menu';
import { negocioDe } from './negocio';
import { NegocioContexto, useNegocio } from './vocabulario';
import { abrirEspacio, cerrarEspacio, type EspacioDeSesion } from './sesion';
import { codigoDeLaUrl, direccionSinCodigo } from './entrarConCodigo';
import { pintarRubro } from './tema';
import { Isotipo } from './logo/Isotipo';

/**
 * La sección va en la dirección.
 *
 * Con `#` y a mano, sin librería de rutas: son once destinos planos, sin
 * anidamiento ni parámetros. Lo que se gana es que el botón de atrás funcione y
 * que alguien pueda dejarse abierto el kardex en una pestaña, que es como se
 * usa esto de verdad.
 */
function seccionDeLaUrl(): Seccion {
  // Solo el primer tramo. Lo que venga detrás es asunto de la pantalla —el
  // kardex lo usa para abrirse en un insumo—, y sin esto `#/kardex/<id>`
  // no era ninguna sección conocida y caía en el resumen.
  const destino = window.location.hash
    .replace('#/', '')
    .split('/')[0] as Seccion;

  return SECCIONES.includes(destino) ? destino : 'resumen';
}

export function App() {
  const [actual, setActual] = useState<{
    session: Session;
    espacio: EspacioDeSesion;
  } | null>(null);
  const session = actual?.session ?? null;

  /*
   * El espacio vivo, para cerrarlo desde los avisos que llegan de fuera de
   * React —la sesión que caduca— sin depender de un estado de hace un render.
   */
  const espacioVivo = useRef<EspacioDeSesion | null>(null);

  const entrar = useCallback((nueva: Session) => {
    if (espacioVivo.current !== null) cerrarEspacio(espacioVivo.current);
    const espacio = abrirEspacio(nueva);
    espacioVivo.current = espacio;
    setActual({ session: nueva, espacio });
  }, []);

  // Salir y perder la sesión acaban igual: lo cargado se borra antes de que
  // la entrada vuelva a la pantalla, no después.
  const soltar = useCallback(() => {
    if (espacioVivo.current !== null) cerrarEspacio(espacioVivo.current);
    espacioVivo.current = null;
    setActual(null);
  }, []);
  const [seccion, setSeccion] = useState<Seccion>(seccionDeLaUrl);
  const [menuAbierto, setMenuAbierto] = useState(false);

  /*
   * Al arrancar, ver si el navegador todavía guarda el refresco.
   *
   * `comprobando` existe para no enseñar la pantalla de entrada durante el
   * parpadeo: quien recarga vería el formulario y empezaría a teclear justo
   * antes de que la sesión vuelva sola.
   */
  const [comprobando, setComprobando] = useState(true);

  const [avisoDeEntrada, setAvisoDeEntrada] = useState<unknown>(null);

  useEffect(() => {
    let vigente = true;

    /*
     * Con el código del celular (`#/entrar?codigo=…`, 9 oct) se entra con él.
     * Primero se borra de la barra y del historial, después se canjea: el
     * código no se queda en una dirección que alguien pueda volver a abrir.
     * Si ya no sirve, a la entrada diciendo por qué.
     */
    const codigo = codigoDeLaUrl(window.location.hash);
    if (codigo !== null) window.history.replaceState(null, '', direccionSinCodigo(window.location));

    const sesion =
      codigo === null
        ? recuperarSesion()
        : canjearCodigo(codigo).catch((fallo: unknown) => {
            if (vigente) setAvisoDeEntrada(fallo);
            return null;
          });

    void sesion.then((recuperada) => {
      if (!vigente) return;
      if (recuperada !== null) entrar(recuperada);
      setComprobando(false);
    });

    return () => {
      vigente = false;
    };
  }, [entrar]);

  // Si la sesión caduca y no se puede renovar, a la entrada: no a una pantalla
  // llena de «no autorizado».
  useEffect(() => {
    onSesionPerdida(soltar);
  }, [soltar]);

  useEffect(() => {
    const alCambiar = () => {
      setSeccion(seccionDeLaUrl());
      // Navegar cierra el cajón. Dejarlo abierto tapa justo la pantalla a la
      // que se acaba de ir.
      setMenuAbierto(false);
    };
    window.addEventListener('hashchange', alCambiar);

    return () => window.removeEventListener('hashchange', alCambiar);
  }, []);

  useEffect(() => {
    if (!menuAbierto) return;

    const alPulsar = (evento: KeyboardEvent) => {
      if (evento.key === 'Escape') setMenuAbierto(false);
    };

    document.addEventListener('keydown', alPulsar);

    return () => document.removeEventListener('keydown', alPulsar);
  }, [menuAbierto]);

  // El rubro y los módulos del local, de la misma sesión que trae los
  // permisos. Sin sesión todavía, lo de siempre: no se pinta nada con ello.
  const negocio = useMemo(() => negocioDe(session?.scope ?? {}), [session]);

  // El color del rubro, solo con alguien dentro: la entrada, antes de saber de
  // quién es el almacén, es de Lucqa (`tema.ts`).
  useEffect(() => pintarRubro(session === null ? null : negocio.rubro), [session, negocio.rubro]);

  if (comprobando) {
    return <div className="aplicacion" />;
  }

  if (actual === null || session === null || !hasSession()) {
    return (
      <Entrada
        aviso={avisoDeEntrada}
        onEntra={(nueva) => {
          setAccessToken(nueva.accessToken);
          entrar(nueva);
        }}
      />
    );
  }

  return (
    // La llave rehace la aplicación entera con cada entrada: ni un dato ni un
    // filtro del negocio anterior sobrevive al cambio de cuenta.
    <QueryClientProvider client={actual.espacio.cliente} key={actual.espacio.llave}>
      <SesionActual.Provider value={session}>
        <NegocioContexto.Provider value={negocio}>
          <div className="aplicacion">
            <BarraLateral
              seccion={seccion}
              session={session}
              abierta={menuAbierto}
              onSalir={() => {
                void cerrarSesion().then(soltar);
              }}
            />

            {/*
             * Por debajo de 900px la barra se esconde, y hasta ahora no la sustituía
             * nada: en una tablet en vertical no había forma de cambiar de sección.
             * El depósito se recorre con tablet, así que era media aplicación
             * inalcanzable para uno de los cuatro perfiles.
             */}
            {menuAbierto ? (
              <div
                className="velo-menu"
                onClick={() => setMenuAbierto(false)}
                aria-hidden="true"
              />
            ) : null}

            <div className="contenido">
              <div className="barra-estrecha">
                <button
                  type="button"
                  className="barra-estrecha__menu"
                  aria-expanded={menuAbierto}
                  aria-label={menuAbierto ? 'Cerrar el menú' : 'Abrir el menú'}
                  onClick={() => setMenuAbierto((previo) => !previo)}
                >
                  <Menu size={18} strokeWidth={2} aria-hidden="true" />
                  Secciones
                </button>
                <Isotipo rubro={negocio.rubro} className="lateral__isotipo" />
              </div>
              <main className="pantalla">
                {pantallaDe(
                  seccionPermitida(seccion, session.scope.permissions, negocio),
                  session.scope.permissions,
                )}
              </main>
            </div>
          </div>
        </NegocioContexto.Provider>
      </SesionActual.Provider>
    </QueryClientProvider>
  );
}

function pantallaDe(seccion: Seccion, permisos: readonly string[]) {
  switch (seccion) {
    case 'resumen':
      return <Resumen />;
    case 'insumos':
      return <Insumos />;
    case 'lotes':
      return <Lotes />;
    case 'reposicion':
      return <Reposicion />;
    case 'recepcion':
      return <Recepcion />;
    case 'carga-inicial':
      return <CargaInicial />;
    case 'precios':
      return <Precios />;
    case 'mermas':
      return <Mermas />;
    case 'cierre-de-noche':
      return <CierreDeNoche />;
    case 'conteo':
      return <Conteo />;
    case 'kardex':
      return <Kardex />;
    case 'valorizacion':
      return <Valorizacion />;
    case 'fuga':
      return <Fuga />;
    case 'costo-real':
      return <CostoReal />;
    case 'cadena':
      return <Cadena />;
    case 'compras':
      return <Compras />;
    case 'proveedores':
      return <Proveedores />;
    case 'produccion':
      return <Produccion />;
    case 'recetas':
      return <Recetas />;
    case 'cuarentena':
      // Las dos acciones solo a quien firma lo descartado; los demás la miran.
      return <Cuarentena puedeDecidir={permisos.includes('inventory.approve')} />;
    case 'libro-de-recetas':
      return <LibroDeRecetas />;
    case 'series':
      return <Series />;
    case 'libro-de-controlados':
      return <LibroDeControlados />;
    case 'cadena-de-frio':
      return <CadenaDeFrio />;
  }
}

function BarraLateral({
  seccion,
  session,
  abierta,
  onSalir,
}: {
  seccion: Seccion;
  session: Session;
  abierta: boolean;
  onSalir: () => void;
}) {
  /*
   * Las insignias se cuentan de los datos, no se escriben.
   *
   * Un número puesto a mano ahí se queda viejo el primer día, y una barra
   * lateral que miente sobre cuántos insumos faltan es peor que una sin
   * números: enseña a no mirarla.
   */
  const negocio = useNegocio();
  const { data: resumen } = useQuery({
    queryKey: ['resumen'],
    queryFn: almacen.summary,
  });

  const insignias: Partial<Record<Seccion, number>> = {
    insumos: resumen?.belowMinimum ?? 0,
    lotes: (resumen?.expiringSoon ?? 0) + (resumen?.expired ?? 0),
  };

  return (
    <nav className={`lateral ${abierta ? 'lateral--abierta' : ''}`}>
      <div className="lateral__marca">
        <Isotipo rubro={negocio.rubro} className="lateral__isotipo" />
        <span className="lateral__logo">lucqa</span>
        <span className="lateral__cara">Almacén</span>
      </div>

      <div className="lateral__nav">
        {menuDe(session.scope.permissions, negocio).map((grupo) => (
          <div key={grupo.grupo} className="lateral__grupo">
            <p className="lateral__grupo-rotulo">{grupo.grupo}</p>
            <div className="lateral__grupo-items">
              {grupo.items.map((item) => {
                const Icono = item.icono;
                const insignia = insignias[item.id] ?? 0;

                return (
                  <a
                    key={item.id}
                    href={`#/${item.id}`}
                    className={`lateral__item ${
                      seccion === item.id ? 'lateral__item--activo' : ''
                    }`}
                    aria-current={seccion === item.id ? 'page' : undefined}
                  >
                    <Icono size={16} strokeWidth={2} />
                    <span className="lateral__item-texto">{item.texto}</span>
                    {insignia > 0 ? (
                      <span className="lateral__insignia">{insignia}</span>
                    ) : null}
                  </a>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      <div className="lateral__local">
        <p className="lateral__local-rotulo">Local</p>
        {/*
         * La empresa antes que la sucursal: un encargado de cadena tiene tres
         * «Miraflores» y sin el nombre comercial no sabe en cuál está moviendo
         * el stock.
         */}
        <p className="lateral__local-nombre">
          {session.scope.companyTradeName} · {session.scope.branchName}
        </p>
      </div>

      <button type="button" className="lateral__usuario" onClick={onSalir}>
        <span className="lateral__avatar" aria-hidden="true">
          {iniciales(session.user.fullName)}
        </span>
        <span className="lateral__usuario-texto">
          <span className="lateral__usuario-nombre">
            {session.user.fullName}
          </span>
          <span className="lateral__usuario-apoyo">Almacén · Salir</span>
        </span>
      </button>
    </nav>
  );
}
